import * as R from 'ramda';

import { PALETTE } from './palette';

const toCss = (variant: any) => ({
  ...variant,
  lineHeight: `${variant.lineHeight}px`,
});

export const FONT_SIZE = 14;
export const HTML_FONT_SIZE = 16;

const FONT_FAMILY = {
  headers: 'Rethink Sans',
  body: 'Inter',
};

const hero1 = {
  fontFamily: FONT_FAMILY.headers,
  fontSize: 68,
  lineHeight: 78,
  fontWeight: 700,
  letterSpacing: -0.5,
  color: PALETTE.night200,
};

const hero2 = {
  fontFamily: FONT_FAMILY.headers,
  fontSize: 54,
  lineHeight: 62,
  fontWeight: 700,
  letterSpacing: -0.5,
  color: PALETTE.night200,
};

const hero3 = {
  fontFamily: FONT_FAMILY.headers,
  fontSize: 48,
  lineHeight: 55,
  fontWeight: 700,
  letterSpacing: -0.5,
  color: PALETTE.night200,
};

const hero4 = {
  fontFamily: FONT_FAMILY.headers,
  fontSize: 40,
  lineHeight: 46,
  fontWeight: 700,
  letterSpacing: -0.5,
  color: PALETTE.night200,
};

const h1 = {
  fontFamily: FONT_FAMILY.headers,
  fontSize: 33,
  lineHeight: 41,
  fontWeight: 700,
  letterSpacing: -0.5,
  color: PALETTE.night200,
};

const h2 = {
  fontFamily: FONT_FAMILY.headers,
  fontSize: 27,
  lineHeight: 34,
  fontWeight: 700,
  letterSpacing: -0.5,
  color: PALETTE.night200,
};

const h3 = {
  fontFamily: FONT_FAMILY.headers,
  fontSize: 23,
  lineHeight: 29,
  fontWeight: 700,
  letterSpacing: 0,
  color: PALETTE.night200,
};

const h4 = {
  fontFamily: FONT_FAMILY.headers,
  fontSize: 19,
  lineHeight: 24,
  fontWeight: 700,
  letterSpacing: 0,
  color: PALETTE.night200,
};

const h5 = {
  fontFamily: FONT_FAMILY.headers,
  fontSize: 16,
  lineHeight: 20,
  fontWeight: 700,
  letterSpacing: 0,
  color: PALETTE.night200,
};

const h6 = {
  fontFamily: FONT_FAMILY.headers,
  fontSize: 13.5,
  lineHeight: 17,
  fontWeight: 600,
  letterSpacing: 0,
  color: PALETTE.night200,
};

const h7 = {
  fontFamily: FONT_FAMILY.headers,
  fontSize: 12,
  lineHeight: 16,
  fontWeight: 600,
  letterSpacing: 0,
  color: PALETTE.night200,
};

const eyebrowHeader1 = {
  fontFamily: FONT_FAMILY.body,
  fontSize: 21,
  lineHeight: 29,
  fontWeight: 500,
  letterSpacing: 1.5,
  color: PALETTE.purple200,
};

const eyebrowHeader2 = {
  fontFamily: FONT_FAMILY.body,
  fontSize: 12,
  lineHeight: 17,
  fontWeight: 500,
  letterSpacing: 0.5,
  color: PALETTE.purple200,
};

const body1 = {
  fontFamily: FONT_FAMILY.body,
  fontSize: 16,
  lineHeight: 26,
  fontWeight: 400,
  letterSpacing: -0.5,
  color: PALETTE.grey200,
};

const body1SemiBold = {
  fontFamily: FONT_FAMILY.body,
  fontSize: 16,
  lineHeight: 26,
  fontWeight: 600,
  color: PALETTE.grey200,
};

const body2 = {
  fontFamily: FONT_FAMILY.body,
  fontSize: 16,
  lineHeight: 22,
  fontWeight: 400,
  letterSpacing: -0.5,
  color: PALETTE.grey200,
};

const body2SemiBold = {
  fontFamily: FONT_FAMILY.body,
  fontSize: 16,
  lineHeight: 22,
  fontWeight: 600,
  letterSpacing: -0.5,
  color: PALETTE.grey200,
};

const body3 = {
  fontFamily: FONT_FAMILY.body,
  fontSize: 13.5,
  lineHeight: 19,
  fontWeight: 400,
  letterSpacing: 0,
  color: PALETTE.grey200,
};

const body3SemiBold = {
  fontFamily: FONT_FAMILY.body,
  fontSize: 13.5,
  lineHeight: 19,
  fontWeight: 600,
  letterSpacing: 0,
  color: PALETTE.grey200,
};

const button1 = {
  fontFamily: FONT_FAMILY.body,
  fontSize: 16,
  lineHeight: 21,
  fontWeight: 600,
  letterSpacing: 0,
  color: PALETTE.night200,
};

const button2 = {
  fontFamily: FONT_FAMILY.body,
  fontSize: 13.5,
  lineHeight: 18,
  fontWeight: 600,
  letterSpacing: 0,
  color: PALETTE.night200,
};

const caption1 = {
  fontFamily: FONT_FAMILY.body,
  fontSize: 12,
  lineHeight: 17,
  fontWeight: 400,
  letterSpacing: 0,
  color: PALETTE.grey200,
};

const caption1Bold = {
  fontFamily: FONT_FAMILY.body,
  fontSize: 12,
  lineHeight: 17,
  fontWeight: 600,
  letterSpacing: 0,
  color: PALETTE.grey200,
};

const caption2 = {
  fontFamily: FONT_FAMILY.body,
  fontSize: 9,
  lineHeight: 13,
  fontWeight: 400,
  letterSpacing: 0,
  color: PALETTE.grey200,
};

const caption2Bold = {
  fontFamily: FONT_FAMILY.body,
  fontSize: 9,
  lineHeight: 13,
  fontWeight: 600,
  letterSpacing: 0,
  color: PALETTE.grey200,
};

export const TYPOGRAPHY = R.mapObjIndexed(toCss, {
  hero1,
  hero2,
  hero3,
  hero4,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  h7,
  eyebrowHeader1,
  eyebrowHeader2,
  body1,
  body1SemiBold,
  body2,
  body2SemiBold,
  body3,
  body3SemiBold,
  button1,
  button2,
  caption1,
  caption1Bold,
  caption2,
  caption2Bold,
});

export const MOBILE_TYPOGRAPHY: Partial<typeof TYPOGRAPHY> = R.mapObjIndexed(
  toCss,
  {
    hero1: hero2,
    hero2: hero3,
    hero3: hero4,
    hero4: h1,
    h1: h2,
    h2: h3,
    h3: h4,
  },
);

export const getResponsiveTypography = (downMdQuery: string) =>
  R.mapObjIndexed((styles, variant) => {
    const mobileStyles = MOBILE_TYPOGRAPHY[variant as keyof typeof TYPOGRAPHY];
    return mobileStyles ? { ...styles, [downMdQuery]: mobileStyles } : styles;
  }, TYPOGRAPHY);

export type TypographyVariant = {
  fontFamily: string;
  fontSize: number;
  lineHeight: string;
  fontWeight: number;
  letterSpacing?: number;
  color?: string;
};
